import React, { useCallback, useEffect, useState } from "react";
import { getUserRole } from "../Routes/ProtectedRoute";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useNavigate } from "react-router-dom";
import {
  createAddonPlan,
  deleteAddonPlan,
  listAddonPlans,
  setAddonPlanStatus,
  updateAddonPlan,
} from "../services/addonPlanApi";

const CAN_MANAGE_ADDONS = new Set(["Hr", "AdminController", "ProjectManager"]);

const EMPTY_FORM = {
  planName: "",
  description: "",
  amount: "",
  totalHoursPerMonth: "",
  status: "active",
  freeMonths: "0",
  autoPayEnabled: false,
  planValidity: "30",
};

function formatInr(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount));
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

function planToForm(plan) {
  return {
    planName: plan?.planName || "",
    description: plan?.description || "",
    amount: plan?.price?.amount ?? "",
    totalHoursPerMonth: plan?.totalHoursPerMonth ?? plan?.hoursPerMonth ?? "",
    status: plan?.status === "inactive" ? "inactive" : plan?.status || "active",
    freeMonths: String(plan?.freeMonths ?? 0),
    autoPayEnabled: Boolean(plan?.autoPayEnabled),
    planValidity: String(plan?.planValidity ?? 30),
  };
}

function formToPayload(form) {
  return {
    planName: form.planName.trim(),
    description: form.description.trim(),
    price: {
      amount: Number(form.amount),
      currency: "INR",
      discount: 0,
    },
    totalHoursPerMonth: Number(form.totalHoursPerMonth),
    status: form.status,
    freeMonths: Number(form.freeMonths) || 0,
    autoPayEnabled: Boolean(form.autoPayEnabled),
    validityMonths: Number(form.planValidity) || 1,
  };
}

function statusChipColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "success";
  if (s === "inactive") return "default";
  if (s === "draft") return "warning";
  return "default";
}

export default function AddonPlansPage() {
  const navigate = useNavigate();
  const canManage = CAN_MANAGE_ADDONS.has(getUserRole());
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await listAddonPlans({
        limit: 100,
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      const body = res.data;
      const rows = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
      setPlans(rows);
    } catch (err) {
      setPlans([]);
      setError(err.response?.data?.message || "Could not load add-on plans.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditingId(plan._id);
    setForm(planToForm(plan));
    setDialogOpen(true);
  };

  const savePlan = async () => {
    if (!form.planName.trim()) {
      setSnackbar({ open: true, message: "Plan name is required.", severity: "error" });
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      setSnackbar({ open: true, message: "Price must be greater than zero.", severity: "error" });
      return;
    }
    if (!form.totalHoursPerMonth || Number(form.totalHoursPerMonth) <= 0) {
      setSnackbar({
        open: true,
        message: "Total hours per month is required.",
        severity: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editingId) {
        await updateAddonPlan(editingId, payload);
        setSnackbar({ open: true, message: "Add-on plan updated.", severity: "success" });
      } else {
        await createAddonPlan(payload);
        setSnackbar({ open: true, message: "Add-on plan created.", severity: "success" });
      }
      setDialogOpen(false);
      loadPlans();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Could not save add-on plan.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (planId, status) => {
    try {
      await setAddonPlanStatus(planId, status);
      setSnackbar({ open: true, message: `Plan marked ${status}.`, severity: "success" });
      loadPlans();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Could not update status.",
        severity: "error",
      });
    }
  };

  const removePlan = async (planId) => {
    if (!window.confirm("Deprecate this add-on plan? Users will no longer be able to purchase it.")) {
      return;
    }
    try {
      await deleteAddonPlan(planId);
      setSnackbar({ open: true, message: "Add-on plan deprecated.", severity: "success" });
      loadPlans();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Could not deprecate plan.",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ p: 4, bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 4, maxWidth: 1400, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <IconButton onClick={() => navigate("/subscription-analysis")}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Add-on Plans
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage UV hour add-on packs · POST /create-addonplan · GET /get-addonplans
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          {canManage ? (
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={openCreate}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              New add-on plan
            </Button>
          ) : null}
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Plan name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Hours</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Validity (days)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
                  {canManage ? (
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Actions
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canManage ? 7 : 6} align="center" sx={{ py: 5, color: "#64748b" }}>
                      No add-on plans yet. Create one so users can purchase extra UV hours.
                    </TableCell>
                  </TableRow>
                ) : (
                  plans.map((plan) => (
                    <TableRow key={plan._id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>{plan.planName}</Typography>
                        {plan.description ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {plan.description}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{plan.totalHoursPerMonth ?? plan.hoursPerMonth ?? "—"} h</TableCell>
                      <TableCell>
                        {formatInr(plan.price?.finalPrice ?? plan.price?.amount)}
                      </TableCell>
                      <TableCell>{plan.planValidity ?? "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={plan.status || "active"}
                          size="small"
                          color={statusChipColor(plan.status)}
                          sx={{ textTransform: "capitalize" }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(plan.updatedAt)}</TableCell>
                      {canManage ? (
                        <TableCell align="right">
                          <Button size="small" onClick={() => openEdit(plan)}>
                            Edit
                          </Button>
                          {plan.status === "draft" ? (
                            <Button size="small" onClick={() => changeStatus(plan._id, "active")}>
                              Activate
                            </Button>
                          ) : null}
                          {plan.status === "active" ? (
                            <Button
                              size="small"
                              color="warning"
                              onClick={() => changeStatus(plan._id, "inactive")}
                            >
                              Deactivate
                            </Button>
                          ) : null}
                          {plan.status !== "inactive" ? (
                            <Button size="small" color="error" onClick={() => removePlan(plan._id)}>
                              Deprecate
                            </Button>
                          ) : null}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Edit add-on plan" : "Create add-on plan"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Plan name"
            value={form.planName}
            onChange={(e) => setForm({ ...form, planName: e.target.value })}
            required
            fullWidth
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            rows={2}
            fullWidth
          />
          <TextField
            label="Price (INR)"
            type="number"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            required
            fullWidth
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Total hours per month"
            type="number"
            value={form.totalHoursPerMonth}
            onChange={(e) => setForm({ ...form, totalHoursPerMonth: e.target.value })}
            required
            fullWidth
            helperText="UV hours included in this add-on pack"
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Plan validity (days)"
            type="number"
            value={form.planValidity}
            onChange={(e) => setForm({ ...form, planValidity: e.target.value })}
            fullWidth
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Free months"
            type="number"
            value={form.freeMonths}
            onChange={(e) => setForm({ ...form, freeMonths: e.target.value })}
            fullWidth
            inputProps={{ min: 0 }}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={form.status}
              label="Status"
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={form.autoPayEnabled}
                onChange={(e) => setForm({ ...form, autoPayEnabled: e.target.checked })}
              />
            }
            label="Auto-pay enabled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePlan} disabled={saving}>
            {saving ? "Saving…" : editingId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
